import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { isNoCoverPlaceholder } from "@/utils/coverPlaceholder";
import { cleanIsbn } from "@/utils/textCleaners";
import { appleArtwork } from "../../supabase/functions/_shared/bookCover";

/**
 * Highest-quality variant of a cover URL, for the lightbox ONLY.
 *
 * Deliberately separate from `getHighQualityDisplayUrl` in utils/performance:
 * that one feeds inline thumbnails everywhere, so raising its resolution would
 * change bytes-on-load across the whole app. Cover art is the point of this
 * view, so here we ask each provider for the biggest asset it serves.
 */
export const getLightboxImageUrl = (url: string): string => {
  if (!url) return "";

  const https = url.startsWith("http://") ? `https://${url.slice(7)}` : url;

  // Google Books — zoom=3 is the largest the content endpoint serves, and
  // edge=curl paints a fake page-curl over the artwork.
  if (https.includes("books.google") || https.includes("googleusercontent.com")) {
    const noCurl = https.replace(/&?edge=curl/g, "");
    return /zoom=\d+/.test(noCurl)
      ? noCurl.replace(/zoom=\d+/, "zoom=3")
      : `${noCurl}${noCurl.includes("?") ? "&" : "?"}zoom=3`;
  }

  // Apple Books artwork is served at whatever WxH the path asks for.
  if (https.includes("mzstatic.com")) {
    return appleArtwork(https, 1200) ?? https;
  }

  // Open Library: -L is the largest size.
  if (https.includes("covers.openlibrary.org")) {
    return https.replace(/-[SM]\.jpg/, "-L.jpg");
  }

  // Shopify (publisher stores, e.g. Gollancz): drop any width/height cap so the
  // original asset is served.
  if (https.includes("/cdn/shop/") || https.includes("cdn.shopify.com")) {
    return https.replace(/([?&])(width|height)=\d+/g, "$1").replace(/[?&]$/, "");
  }

  return https;
};

/**
 * Mid-quality step. Google Books does not hold a zoom=3 scan for every edition —
 * it answers with its "image not available" graphic instead. Falling straight
 * back to the original meant a 128px thumbnail filling the enlarged view, so try
 * zoom=2 in between before giving up on resolution.
 */
export const getMediumImageUrl = (url: string): string => {
  if (!url) return "";
  const https = url.startsWith("http://") ? `https://${url.slice(7)}` : url;
  if (https.includes("books.google") || https.includes("googleusercontent.com")) {
    const noCurl = https.replace(/&?edge=curl/g, "");
    return /zoom=\d+/.test(noCurl)
      ? noCurl.replace(/zoom=\d+/, "zoom=2")
      : `${noCurl}${noCurl.includes("?") ? "&" : "?"}zoom=2`;
  }
  return getLightboxImageUrl(https);
};

interface CoverLightboxProps {
  src: string;
  /**
   * The book title, on its own. Callers used to pass `${title} cover`, which was
   * then printed verbatim under the image — "Dune cover" — and folded into the
   * dialog's accessible name as "Dune cover — enlarged cover". The component
   * derives both from the bare title so no caller has to know the wording.
   */
  title: string;
  /**
   * Needed only to find a better-resolution cover when the stored one is too
   * small for the fixed size. Without it the search is skipped entirely rather
   * than matched on title alone, which pulls in other editions and other books.
   */
  author?: string;
  /**
   * The edition's ISBN. Without it no cover upgrade is attempted at all — see
   * isSameEdition. Callers that genuinely have no ISBN simply show the stored
   * cover, which is what the card beside them is showing anyway.
   */
  isbn?: string;
  onClose: () => void;
}

/**
 * Enlarged view of a book cover. Cover art matters, so this shows the largest
 * asset the provider has rather than upscaling the thumbnail.
 *
 * Deliberately NOT full-bleed — the image sits at up to 80vh/88vw so the art is
 * comfortably readable while the page stays visible behind it. Dismiss by
 * tapping the backdrop, pressing Escape, or swiping down (matching the
 * protagonist portrait lightbox).
 */
export function CoverLightbox({ src, title, author, isbn, onClose }: CoverLightboxProps) {
  const [loaded, setLoaded] = useState(false);
  /**
   * A better-resolution replacement found after the fact, when the stored cover
   * turned out too small to fill the fixed size cleanly.
   *
   * Most of this catalogue is Open Library's -L variant at roughly 500px tall.
   * At 68vh on a large display that is around a third of upscaling, which is
   * exactly the softness you see on Ubik and Time Out of Joint. The size is
   * deliberate and shared with the rest of the site, so the answer is a bigger
   * SOURCE rather than a smaller box.
   */
  const [upgraded, setUpgraded] = useState<string | null>(null);
  const triedUpgrade = useRef(false);
  // Asking a provider for its largest asset can miss: Google Books returns its
  // "image not available" graphic for editions that have no zoom=3 scan, so the
  // enlarged view showed a placeholder even though the thumbnail on the card was
  // fine. Fall back to the original URL in that case rather than show the fake.
  // 0 = largest the provider offers, 1 = mid, 2 = the URL as stored.
  const [step, setStep] = useState(0);
  /**
   * Below this the fixed 68vh view is visibly upscaling on a normal display, so
   * it is worth one search for something better. Open Library's -L tops out
   * around 500px, which is why so many covers land under it.
   */
  const MIN_GOOD_HEIGHT = 700;

  /**
   * Same EDITION, not merely the same book.
   *
   * This compared TITLES, and every edition of a book shares its title, so the
   * guard could never reject one. Observed on Ammonite: the card showed Del Rey's
   * cover (ISBN 9780345452382) and enlarging swapped in Gollancz's SF Masterworks
   * artwork (9780575118249), because a Google Books title search returns editions
   * and the first one happened to be Gollancz. Author does not separate them
   * either. The ISBN does, and GoogleBook already carries one.
   *
   * A missing or differently-formatted ISBN (ISBN-10 on one side, ISBN-13 on the
   * other) fails to match and so skips the upgrade. That is the safe direction:
   * a soft cover that agrees with the card beats a sharp one that does not.
   */
  const isSameEdition = (candidateIsbn?: string): boolean => {
    if (!isbn || !candidateIsbn) return false;
    return cleanIsbn(candidateIsbn) === cleanIsbn(isbn);
  };

  const upgradeIfLowRes = async (loadedHeight: number) => {
    if (triedUpgrade.current) return;
    if (!loadedHeight || loadedHeight >= MIN_GOOD_HEIGHT) return;
    // No ISBN, no upgrade: without one there is no way to tell a sharper scan of
    // THIS edition from a different edition's cover.
    if (!isbn) return;
    if (!author) return; // title alone matches too many editions to be safe
    triedUpgrade.current = true;

    try {
      const { searchGoogleBooks } = await import('@/services/googleBooks');
      const results = await searchGoogleBooks(`${title} ${author}`, 5);
      const better = results.find((r) => r.coverUrl && isSameEdition(r.isbn));
      if (better?.coverUrl) {
        // zoom=3 via the same ladder the first attempt used.
        setUpgraded(getLightboxImageUrl(better.coverUrl));
      }
    } catch {
      /* the stored cover already renders; a failed upgrade changes nothing */
    }
  };

  const dragStartY = useRef<number | null>(null);
  const dragY = useRef(0);
  const sheetRef = useRef<HTMLDivElement>(null);

  // Escape to close, and hold page scroll while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const handleTouchStart = (e: React.TouchEvent) => {
    dragStartY.current = e.touches[0].clientY;
    dragY.current = 0;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (dragStartY.current === null) return;
    const delta = e.touches[0].clientY - dragStartY.current;
    if (delta <= 0) return; // downward swipes only
    dragY.current = delta;
    if (sheetRef.current) {
      sheetRef.current.style.transform = `translateY(${delta}px)`;
      sheetRef.current.style.opacity = String(Math.max(0, 1 - delta / 320));
    }
  };

  const handleTouchEnd = () => {
    if (dragY.current > 110) {
      onClose();
      return;
    }
    if (sheetRef.current) {
      sheetRef.current.style.transform = "";
      sheetRef.current.style.opacity = "";
    }
    dragStartY.current = null;
    dragY.current = 0;
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-md p-6 animate-in fade-in duration-200"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${title} — enlarged cover`}
    >
      <button
        onClick={onClose}
        aria-label="Close enlarged cover"
        /* 44px touch target with a soft translucent fill instead of a hard
           1px border — at mobile DPR the old border rendered as a gritty ring
           around a too-small button. grid+place-items keeps the X optically
           centred rather than relying on padding. */
        className="absolute top-4 right-4 grid place-items-center w-11 h-11 rounded-full bg-slate-900/60 backdrop-blur-sm ring-1 ring-white/10 text-slate-200 hover:bg-slate-900/80 hover:text-white active:scale-95 transition-all"
      >
        <X className="w-5 h-5" strokeWidth={2.25} />
      </button>

      <div
        ref={sheetRef}
        className="flex flex-col items-center transition-transform"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="w-10 h-1 rounded-full bg-slate-600 mb-4 sm:hidden" />

        {!loaded && (
          <div className="w-48 h-72 rounded-lg bg-slate-800/60 border border-slate-700 animate-pulse" />
        )}
        <img
          key={upgraded ?? step}
          src={
            upgraded ??
            (step === 0 ? getLightboxImageUrl(src) : step === 1 ? getMediumImageUrl(src) : src)
          }
          alt={`${title} cover`}
          onLoad={(e) => {
            // A placeholder still loads with HTTP 200, so detect it by dimension
            // (same check the film-cover recovery uses) and retry the original.
            if (step < 2 && isNoCoverPlaceholder(e.currentTarget)) {
              setStep(step + 1);
              return;
            }
            const img = e.currentTarget;
            setLoaded(true);
            void upgradeIfLowRes(img.naturalHeight);
          }}
          onError={() => {
            if (step < 2) { setStep(step + 1); return; }
            setLoaded(true);
          }}
          /* One size everywhere. Every cover opens at the same height across the
             site and only the aspect ratio varies — that consistency is the
             point of the control, so a low-resolution source is answered by
             FINDING A BETTER SOURCE (see upgradeIfLowRes below), never by
             quietly shrinking this one and breaking the rule for one book. */
          className={`h-[68vh] max-w-[88vw] w-auto object-contain rounded-lg border border-slate-700/60 shadow-2xl shadow-black/60 ${
            loaded ? "block" : "hidden"
          }`}
        />
        <p className="mt-4 text-sm text-slate-400 text-center max-w-[88vw]">{title}</p>
      </div>
    </div>,
    document.body
  );
}

export default CoverLightbox;
